import React from 'react';
import { Breadcrumb, BreadcrumbItem } from '@patternfly/react-core';
import { useViewportSize } from '@ole-ui/ole-ui-components';
import { Link } from 'react-mobx-router5';
import { CourseStructureItem } from 'types/course';
import useMobxStores from 'hooks/useMobxStores';
import { useTranslation } from 'react-i18next';
import { withErrorBoundary } from 'react-error-boundary';
import { observer } from 'mobx-react';
import { handleError } from 'services/ErrorService';
import CourseStore from './store';

type Props = {
  courseStore: CourseStore;
  showCurrentSection?: boolean;
};

function CourseBreadcrumb({ courseStore, showCurrentSection = false }: Props) {
  const { t } = useTranslation();
  const { routerStore, catalogStore } = useMobxStores();
  const isMobile = useViewportSize().isLowerThanMd;

  const { currentSection, currentChapter, firstSection, originRoute } =
    courseStore;

  const formatSectionTitle = (section: CourseStructureItem) => {
    let finalTitle = section?.title;
    if (isMobile) {
      finalTitle = section?.title?.split(':')?.[0] || section.title;
    }
    return finalTitle;
  };

  const routeName = !['courses:overview', ''].includes(originRoute.to)
    ? originRoute.to
    : 'catalog';

  const routeLabel =
    routeName === 'catalog' ? t('Catalog') : t(originRoute.label);

  return (
    <Breadcrumb>
      <BreadcrumbItem className="hud-course-view__fade-in" to={originRoute.to}>
        <Link
          href
          routeName={routeName}
          routeParams={catalogStore.lastCatalogParams}
        >
          {routeLabel || t('Catalog')}
        </Link>
      </BreadcrumbItem>
      {firstSection?.page_tag && (
        <BreadcrumbItem
          className="hud-course-view__fade-in hud-course-view__fade-in--delayed"
          component={Link}
          to="courses:page"
          onClick={() =>
            routerStore.navigate('courses:page', {
              course: (routerStore.route as any).params.course,
              page: firstSection.page_tag,
            })
          }
        >
          {courseStore.title || t('Your course')}
        </BreadcrumbItem>
      )}
      {currentChapter?.page_tag && (
        <BreadcrumbItem
          className="hud-course-view__fade-in hud-course-view__fade-in--delayed"
          component={Link}
          isActive={currentChapter.page_tag === currentSection?.page_tag}
          to="courses:page"
          onClick={() => {
            routerStore.navigate('courses:page', {
              course: (routerStore.route as any).params.course,
              page:
                currentChapter.page_tag ||
                (routerStore.route as any).params.course,
            });
          }}
        >
          {formatSectionTitle(currentChapter)}
        </BreadcrumbItem>
      )}
      {showCurrentSection && currentSection && (
        <BreadcrumbItem
          className="hud-course-view__fade-in hud-course-view__fade-in--delayed"
          isActive
        >
          {formatSectionTitle(currentSection)}
        </BreadcrumbItem>
      )}
    </Breadcrumb>
  );
}

export default withErrorBoundary(
  observer(CourseBreadcrumb),
  () => null,
  handleError,
);
